import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { KeyRound, Upload, RefreshCw, Mail, Pin, Trash2, Save, X, AlertTriangle, Search, Plus, ChevronRight, Download } from "lucide-react";
import { toast } from "sonner";

const STATE_LABEL = {
  over_allowance:       "Over allowance",
  within_allowance:     "Within allowance",
  exactly_matched:      "Exact match",
  no_purchase_configured: "Not configured",
};
const STATE_PILL = {
  over_allowance:         "bg-red-100 text-red-800 border-red-200",
  within_allowance:       "bg-emerald-100 text-emerald-800 border-emerald-200",
  exactly_matched:        "bg-blue-100 text-blue-800 border-blue-200",
  no_purchase_configured: "bg-amber-100 text-amber-800 border-amber-200",
};

const PROV_PILL = {
  active:     "bg-emerald-100 text-emerald-800",
  canceled:   "bg-slate-100 text-slate-600",
  cancelled:  "bg-slate-100 text-slate-600",
  "rma issued": "bg-orange-100 text-orange-800",
};

function fmt(n) { return (n === null || n === undefined) ? "—" : Number(n).toLocaleString(); }

// Render a stored UTC ISO ("2026-09-28T14:33:55Z") in the browser's local
// timezone. Backend stores everything in UTC; the UI was previously slicing
// the string raw which meant BST users saw times an hour behind.
function fmtLocal(iso) {
  if (!iso) return "—";
  const s = String(iso);
  const withZ = /[Zz]|[+\-]\d{2}:?\d{2}$/.test(s) ? s : s + "Z";
  const d = new Date(withZ);
  if (isNaN(d.getTime())) return s.slice(0, 16).replace("T", " ");
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function LicenceUsage() {
  const [tab, setTab] = useState("current");
  return (
    <div className="p-6 space-y-6 max-w-[1500px] mx-auto" data-testid="licence-usage-page">
      <div className="flex items-center gap-3">
        <KeyRound className="h-6 w-6 text-indigo-600" />
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Licence Usage</h1>
          <p className="text-xs text-slate-500 mt-0.5">Barracuda MSP licence tracking · supplier + customer allowances</p>
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="current" data-testid="tab-current-report">Current report</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="tab-purchases">Purchases</TabsTrigger>
          <TabsTrigger value="aliases" data-testid="tab-aliases">Customer aliases</TabsTrigger>
          <TabsTrigger value="pooled" data-testid="tab-pooled">Pooled groups</TabsTrigger>
          <TabsTrigger value="product-names" data-testid="tab-product-names">Product names</TabsTrigger>
          <TabsTrigger value="mailbox" data-testid="tab-mailbox">Mailbox</TabsTrigger>
        </TabsList>
        <TabsContent value="current" className="mt-4"><CurrentReport /></TabsContent>
        <TabsContent value="history" className="mt-4"><HistoryTab /></TabsContent>
        <TabsContent value="purchases" className="mt-4"><PurchasesTab /></TabsContent>
        <TabsContent value="aliases" className="mt-4"><AliasesTab /></TabsContent>
        <TabsContent value="pooled" className="mt-4"><PooledGroupsTab /></TabsContent>
        <TabsContent value="product-names" className="mt-4"><ProductNamesTab /></TabsContent>
        <TabsContent value="mailbox" className="mt-4"><MailboxTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Current report                                                     */
/* ------------------------------------------------------------------ */
function CurrentReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [reports, setReports] = useState([]);
  // Filters
  const [q, setQ] = useState("");
  const [productFilter, setProductFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [showInactive, setShowInactive] = useState(false);

  const load = async (id) => {
    setLoading(true);
    try {
      const url = id ? `/barracuda/reports/${id}` : "/barracuda/reports/latest";
      const r = await api.get(url);
      setData(r.data);
      if (r.data?.report?.id) setSelectedReportId(r.data.report.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
    } finally { setLoading(false); }
  };
  const loadReports = async () => {
    const r = await api.get("/barracuda/reports");
    setReports(r.data || []);
  };

  useEffect(() => { load(); loadReports(); }, []);

  const upload = async (evt) => {
    const f = evt.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/barracuda/reports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data.skipped_duplicate) toast.info(`Skipped — this file was already imported on ${r.data.report_date}`);
      else toast.success(`Imported ${r.data.record_count} records`);
      await load();
      await loadReports();
    } catch (e) {
      const errs = e?.response?.data?.detail?.errors;
      if (errs?.length) toast.error(`Validation failed (${errs.length} issue${errs.length === 1 ? "" : "s"}) — nothing imported`);
      else toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Import failed");
    } finally { evt.target.value = ""; }
  };

  const fetchNow = async () => {
    try {
      const r = await api.post("/barracuda/fetch-now");
      if (r.data?.skipped) { toast.warning(`Skipped: ${r.data.reason}`); return; }
      if (r.data?.graph_error) { toast.error(`Graph error: ${r.data.graph_error}`); await load(); await loadReports(); return; }
      const imp = (r.data.imported || []).length;
      const errs = (r.data.errors || []);
      if (errs.length) toast.error(`Checked ${r.data.checked_messages || 0} · imported ${imp} · errors: ${(errs[0].error || "").slice(0,140)}`);
      else toast.success(`Checked ${r.data.checked_messages || 0} messages · ${imp} imported`);
      await load();
      await loadReports();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d ? JSON.stringify(d).slice(0,200) : `Fetch failed (${e?.response?.status || "network"})`));
    }
  };

  if (loading) return <div className="text-sm text-slate-500 p-6">Loading…</div>;
  if (!data || !data.report) return (
    <Card className="border-slate-200">
      <CardContent className="p-8 text-center">
        <KeyRound className="h-8 w-8 text-slate-300 mx-auto" />
        <div className="mt-2 text-sm text-slate-500">No licence reports yet.</div>
        <div className="mt-4 flex justify-center gap-2">
          <label className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 text-sm cursor-pointer" data-testid="upload-csv-empty">
            <Upload className="h-4 w-4" /> Upload CSV
            <input type="file" accept=".csv" className="hidden" onChange={upload} />
          </label>
          <Button variant="outline" onClick={fetchNow} data-testid="check-for-reports-empty" className="gap-2"><Mail className="h-4 w-4" /> Check for new reports</Button>
        </div>
      </CardContent>
    </Card>
  );

  const rep = data.report;
  const supplier = data.supplier_summary || [];
  const rows = data.customer_comparison || [];

  const products = Array.from(new Set(rows.map((r) => r.product_code))).sort();
  const groups = Array.from(new Set(rows.map((r) => r.unique_id).filter(Boolean))).sort();
  const filtered = rows.filter((r) => {
    if (productFilter !== "all" && r.product_code !== productFilter) return false;
    if (groupFilter !== "all" && (r.unique_id || "") !== groupFilter) return false;
    if (stateFilter !== "all" && r.state !== stateFilter) return false;
    if (!showInactive && r.inactive_records > 0 && r.active_usage === 0) return false;
    if (q) {
      const t = q.toLowerCase();
      if (!(r.customer.toLowerCase().includes(t) || r.product_code.toLowerCase().includes(t))) return false;
    }
    return true;
  });

  const exportCsv = () => {
    const headers = ["Customer", "Account matched?", "Product", "Product Code", "Contract group",
                     "State", "Purchased", "Active usage", "Difference", "Inactive records", "Statuses"];
    const rows2 = filtered.map((r) => [
      r.customer, r.account_id ? "yes" : "no", r.product_friendly || r.product, r.product_code, r.unique_id || "",
      STATE_LABEL[r.state] || r.state,
      r.purchased_qty ?? "", r.active_usage, r.difference ?? "",
      r.inactive_records, (r.statuses || []).join(" · "),
    ]);
    const esc = (v) => {
      const s = String(v ?? "").replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csv = [headers.map(esc).join(","), ...rows2.map((r) => r.map(esc).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `licence-usage-${rep.report_date || "report"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-mono">Report date</div>
            <div className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
              {rep.report_date}
              {rep.report_date_uncertain && (
                <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 gap-1">
                  <AlertTriangle className="h-3 w-3" /> date uncertain
                </Badge>
              )}
              {rep.is_default_snapshot && (
                <Badge className="bg-blue-100 text-blue-800 border-blue-200 gap-1">
                  <Pin className="h-3 w-3" /> Default view
                </Badge>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-mono">Records</div>
            <div className="font-heading text-lg font-bold text-slate-900">{fmt(rep.record_count)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-mono">Imported</div>
            <div className="text-sm text-slate-700">{fmtLocal(rep.imported_at)}<span className="text-xs text-slate-400 ml-1">by {rep.imported_by}</span></div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedReportId || ""} onValueChange={(v) => load(v)}>
            <SelectTrigger className="w-[220px]" data-testid="report-snapshot-select"><SelectValue placeholder="Snapshot" /></SelectTrigger>
            <SelectContent>
              {reports.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.report_date} — {r.source}{r.is_default_snapshot ? " (default)" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="inline-flex items-center gap-2 border border-slate-300 rounded-md px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50" data-testid="upload-csv-btn">
            <Upload className="h-4 w-4" /> Upload CSV
            <input type="file" accept=".csv" className="hidden" onChange={upload} />
          </label>
          <Button variant="outline" onClick={fetchNow} data-testid="check-for-reports-btn" className="gap-2"><Mail className="h-4 w-4" /> Check for new reports</Button>
        </div>
      </div>

      {/* Supplier summary */}
      <Card className="border-slate-200" data-testid="supplier-summary-card">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">Supplier contract summary</CardTitle>
          <div className="text-xs text-slate-500 mt-1">One row per (product code + contract group). Active licences only.</div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">Code</th>
                  <th className="px-3 py-2">Contract group</th>
                  <th className="px-3 py-2 text-right">Supplier qty</th>
                  <th className="px-3 py-2 text-right">Active</th>
                  <th className="px-3 py-2 text-right">Remaining</th>
                  <th className="px-3 py-2 text-right">Overage</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {supplier.map((g) => (
                  <tr key={g.group_key} className="border-b border-slate-100" data-testid={`supplier-row-${g.group_key}`}>
                    <td className="px-3 py-2 text-slate-700">{g.product_friendly || g.product}{g.product_friendly && <div className="text-[10px] text-slate-400">{g.product}</div>}</td>
                    <td className="px-3 py-2 font-mono text-xs">{g.product_code}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {g.unique_id || <span className="text-slate-400 italic">no group</span>}
                      {g.conflicting_contract_values && (
                        <span className="ml-2 inline-flex items-center gap-1 text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 text-[10px]">
                          <AlertTriangle className="h-3 w-3" /> conflict: {g.conflicting_contract_values.join(" / ")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(g.supplier_contract_qty)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(g.active_usage)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${g.remaining < 0 ? "text-red-600 font-bold" : "text-emerald-700"}`}>{g.remaining > 0 ? "+" : ""}{fmt(g.remaining)}</td>
                    <td className="px-3 py-2 text-right font-mono">{g.overage ? <span className="text-red-600">{fmt(g.overage)}</span> : "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${g.status === "over" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>{g.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input placeholder="Search customer or product…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="licence-search-input" />
            </div>
            <Select value={productFilter} onValueChange={setProductFilter}>
              <SelectTrigger className="w-[200px]" data-testid="filter-product"><SelectValue placeholder="Product" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All products</SelectItem>
                {products.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={groupFilter} onValueChange={setGroupFilter}>
              <SelectTrigger className="w-[180px]" data-testid="filter-group"><SelectValue placeholder="Contract group" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All groups</SelectItem>
                {groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className="w-[200px]" data-testid="filter-state"><SelectValue placeholder="State" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states</SelectItem>
                {Object.entries(STATE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="inline-flex items-center gap-1.5 text-sm ml-2 cursor-pointer">
              <Checkbox checked={showInactive} onCheckedChange={setShowInactive} data-testid="filter-show-inactive" />
              <span>Include inactive-only customers</span>
            </label>
            <div className="flex-1"></div>
            <Button variant="outline" onClick={exportCsv} className="gap-2 ml-auto" data-testid="export-csv-btn"><Download className="h-4 w-4" /> CSV</Button>
          </div>
        </CardContent>
      </Card>

      {/* Customer comparison table */}
      <Card className="border-slate-200" data-testid="customer-comparison-card">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">Customer allowances ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Product</th>
                  <th className="px-3 py-2">Contract group</th>
                  <th className="px-3 py-2">Statuses</th>
                  <th className="px-3 py-2 text-right">Purchased</th>
                  <th className="px-3 py-2 text-right">Active</th>
                  <th className="px-3 py-2 text-right">Difference</th>
                  <th className="px-3 py-2">State</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`customer-row-${i}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{r.customer}</div>
                      <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                        {r.unmatched_customer ? <span className="text-red-600">Unmatched to CRM</span> : <span className="text-emerald-700">Matched</span>}
                        {r.needs_pooled_review && <span className="text-amber-800 bg-amber-100 border border-amber-200 rounded px-1">needs pooled review</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600"><div>{r.product_friendly || r.product}</div><div className="font-mono text-[10px] text-slate-400">{r.product_code}{r.product_friendly ? "" : ""}</div></td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.unique_id || <span className="text-slate-400 italic">—</span>}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(r.statuses || []).map((s) => (
                          <span key={s} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${PROV_PILL[s.toLowerCase()] || "bg-slate-100 text-slate-700"}`}>{s}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {r.purchased_qty === null || r.purchased_qty === undefined ? <span className="text-amber-700 text-xs">Not configured</span> : fmt(r.purchased_qty)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(r.active_usage)}</td>
                    <td className={`px-3 py-2 text-right font-mono ${(r.difference || 0) < 0 ? "text-red-600 font-bold" : (r.difference || 0) > 0 ? "text-emerald-700" : "text-slate-600"}`}>
                      {r.difference === null || r.difference === undefined ? "—" : (r.difference > 0 ? `+${r.difference}` : r.difference)}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${STATE_PILL[r.state] || "bg-slate-100 text-slate-700"}`}>{STATE_LABEL[r.state] || r.state}</span>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="8" className="py-8 text-center text-sm text-slate-500">No customers match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* History                                                            */
/* ------------------------------------------------------------------ */
function HistoryTab() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seedUrl, setSeedUrl] = useState("https://customer-assets-jt897jd0.emergentagent.net/job_xentra-stax/artifacts/6kek2tnn_Barracuda%20MSP%20Licence%20Usage.xlsx");
  const [seedBusy, setSeedBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/barracuda/reports");
      setReports(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pin = async (id) => {
    await api.post(`/barracuda/reports/${id}/pin-default`);
    toast.success("Pinned as default");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this snapshot? Records are removed permanently.")) return;
    await api.delete(`/barracuda/reports/${id}`);
    toast.success("Deleted");
    load();
  };
  const seed = async () => {
    setSeedBusy(true);
    try {
      const r = await api.post("/barracuda/seed-from-workbook", { xlsx_url: seedUrl });
      const results = r.data.results || [];
      const ok = results.filter((x) => x.report_id).length;
      toast.success(`Imported ${ok} historical snapshot${ok === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally { setSeedBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">Import history</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-sm text-slate-500">Loading…</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2">Report date</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2">Filename</th>
                  <th className="px-3 py-2">Email received</th>
                  <th className="px-3 py-2">Imported</th>
                  <th className="px-3 py-2 text-right">Records</th>
                  <th className="px-3 py-2">Result</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100" data-testid={`history-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono">
                      {r.report_date}{r.report_date_uncertain && <AlertTriangle className="h-3 w-3 inline-block ml-1 text-amber-600" />}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className="uppercase tracking-wider text-slate-500">{r.source}</span>
                      {r.is_default_snapshot && <Badge className="ml-2 bg-blue-100 text-blue-800 gap-1"><Pin className="h-3 w-3" /> default</Badge>}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-[240px] truncate">{r.source_filename || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.email_received_at ? fmtLocal(r.email_received_at) : "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{fmtLocal(r.imported_at)}<div className="text-[10px] text-slate-400">by {r.imported_by}</div></td>
                    <td className="px-3 py-2 text-right font-mono">{r.record_count}</td>
                    <td className="px-3 py-2 text-xs">{r.has_errors ? <span className="text-red-600">errors</span> : <span className="text-emerald-700">ok</span>}</td>
                    <td className="px-3 py-2 text-right">
                      {!r.is_default_snapshot && <Button variant="outline" size="sm" onClick={() => pin(r.id)} className="gap-1" data-testid={`pin-${r.id}`}><Pin className="h-3 w-3" />Pin</Button>}
                      <Button variant="outline" size="sm" onClick={() => del(r.id)} className="ml-1 text-red-600 gap-1" data-testid={`delete-${r.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </td>
                  </tr>
                ))}
                {reports.length === 0 && <tr><td colSpan="8" className="py-6 text-center text-sm text-slate-500">No reports imported yet.</td></tr>}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="seed-workbook-card">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">Bootstrap from historic workbook</CardTitle>
          <div className="text-xs text-slate-500 mt-1">
            One-off backfill — reads each dated sheet of a Barracuda usage workbook and imports it as a historical snapshot. Purchases tab seeds "needs product review" rows.
          </div>
        </CardHeader>
        <CardContent className="p-4 flex gap-2">
          <Input value={seedUrl} onChange={(e) => setSeedUrl(e.target.value)} data-testid="seed-workbook-url" />
          <Button onClick={seed} disabled={seedBusy} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="seed-workbook-btn"><RefreshCw className={`h-4 w-4 ${seedBusy ? "animate-spin" : ""}`} />{seedBusy ? "Seeding…" : "Seed history"}</Button>
        </CardContent>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Purchases                                                          */
/* ------------------------------------------------------------------ */
function PurchasesTab() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/barracuda/purchases");
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this purchase record?")) return;
    await api.delete(`/barracuda/purchases/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">Customer purchases power the "Purchased vs Active" comparison. Rows flagged <span className="font-mono text-amber-700">needs review</span> don't count until you assign a product.</div>
        <Button className="gap-2 bg-blue-600 hover:bg-blue-700" onClick={() => { setEditing(null); setShowAdd(true); }} data-testid="add-purchase-btn"><Plus className="h-4 w-4" /> Add purchase</Button>
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Product code</th>
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2 text-right">Quantity</th>
                <th className="px-3 py-2">Effective from</th>
                <th className="px-3 py-2">Needs review</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className={`border-b border-slate-100 ${p.needs_review ? "bg-amber-50/50" : ""}`} data-testid={`purchase-row-${p.id}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">{p.customer_alias}</td>
                  <td className="px-3 py-2 font-mono text-xs">{p.product_code}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs">{p.product_name || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(p.quantity)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{p.effective_from || "—"}</td>
                  <td className="px-3 py-2">{p.needs_review ? <Badge className="bg-amber-100 text-amber-800 border-amber-200">Yes</Badge> : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="outline" size="sm" onClick={() => { setEditing(p); setShowAdd(true); }} data-testid={`edit-purchase-${p.id}`}>Edit</Button>
                    <Button variant="outline" size="sm" className="ml-1 text-red-600" onClick={() => del(p.id)} data-testid={`delete-purchase-${p.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="7" className="py-8 text-center text-slate-500 text-sm">No purchases configured.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {showAdd && <PurchaseDialog editing={editing} onClose={() => { setShowAdd(false); setEditing(null); }} onSaved={load} />}
    </div>
  );
}

function PurchaseDialog({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || { customer_alias: "", product_code: "", product_name: "", quantity: 0, effective_from: "", needs_review: false, notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.customer_alias || !form.product_code || !form.quantity) return toast.error("Customer, product code and quantity are required");
    setBusy(true);
    try {
      if (editing?.id) await api.patch(`/barracuda/purchases/${editing.id}`, form);
      else await api.post("/barracuda/purchases", form);
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="purchase-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit purchase" : "Add purchase"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Customer name (as it appears in Barracuda reports)</Label>
            <Input value={form.customer_alias} onChange={(e) => setForm({ ...form, customer_alias: e.target.value })} data-testid="purchase-customer-alias" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Product code</Label>
              <Input value={form.product_code} onChange={(e) => setForm({ ...form, product_code: e.target.value.toUpperCase() })} placeholder="BEO-CCB1-MSP" data-testid="purchase-product-code" />
            </div>
            <div>
              <Label className="text-xs">Quantity</Label>
              <Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value || "0", 10) })} data-testid="purchase-quantity" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Product name (optional)</Label>
            <Input value={form.product_name || ""} onChange={(e) => setForm({ ...form, product_name: e.target.value })} data-testid="purchase-product-name" />
          </div>
          <div>
            <Label className="text-xs">Effective from</Label>
            <Input type="date" value={form.effective_from || ""} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="purchase-effective-from" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <Checkbox checked={!!form.needs_review} onCheckedChange={(v) => setForm({ ...form, needs_review: !!v })} data-testid="purchase-needs-review" />
            <span>Needs review (don't count against comparisons yet)</span>
          </label>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="purchase-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="purchase-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="purchase-save"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Aliases                                                            */
/* ------------------------------------------------------------------ */
function AliasesTab() {
  const [items, setItems] = useState([]);
  const [alias, setAlias] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);

  const load = async () => {
    const [al, ac] = await Promise.all([api.get("/barracuda/aliases"), api.get("/accounts")]);
    setItems(al.data || []);
    setAccounts(ac.data || []);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!alias || !accountId) return toast.error("Both fields required");
    try {
      await api.post("/barracuda/aliases", { alias, account_id: accountId });
      toast.success("Alias saved");
      setAlias(""); setAccountId("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    await api.delete(`/barracuda/aliases/${id}`);
    load();
  };

  return (
    <div className="space-y-3">
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100"><CardTitle className="text-base font-heading">Add alias</CardTitle></CardHeader>
        <CardContent className="p-4 flex gap-2 items-end">
          <div className="flex-1">
            <Label className="text-xs">Customer name in Barracuda</Label>
            <Input value={alias} onChange={(e) => setAlias(e.target.value)} data-testid="alias-name-input" />
          </div>
          <div className="flex-1">
            <Label className="text-xs">CRM account</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger data-testid="alias-account-select"><SelectValue placeholder="Pick account" /></SelectTrigger>
              <SelectContent>{accounts.slice(0, 500).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button className="gap-2 bg-blue-600 hover:bg-blue-700" onClick={add} data-testid="alias-add-btn"><Plus className="h-4 w-4" />Add</Button>
        </CardContent>
      </Card>
      <Card className="border-slate-200"><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
            <tr><th className="px-3 py-2">Alias</th><th className="px-3 py-2">Account ID</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-b border-slate-100">
                <td className="px-3 py-2 font-medium">{a.alias}</td>
                <td className="px-3 py-2 font-mono text-xs">{a.account_id}</td>
                <td className="px-3 py-2 text-right"><Button variant="outline" size="sm" className="text-red-600" onClick={() => del(a.id)} data-testid={`alias-delete-${a.id}`}><Trash2 className="h-3 w-3" /></Button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="3" className="py-6 text-center text-sm text-slate-500">No aliases configured.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pooled groups                                                      */
/* ------------------------------------------------------------------ */
function PooledGroupsTab() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [members, setMembers] = useState("");
  const [productCode, setProductCode] = useState("");

  const load = async () => setItems((await api.get("/barracuda/pooled-groups")).data || []);
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return toast.error("Name required");
    const list = members.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    await api.post("/barracuda/pooled-groups", { name, product_code: productCode || null, member_customer_names: list });
    setName(""); setMembers(""); setProductCode("");
    toast.success("Group added");
    load();
  };
  const del = async (id) => { await api.delete(`/barracuda/pooled-groups/${id}`); load(); };

  return (
    <div className="space-y-3">
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100"><CardTitle className="text-base font-heading">Add pooled group</CardTitle>
          <div className="text-xs text-slate-500 mt-1">A pooled group lets several Barracuda customer names share one allowance (e.g. Wigmore Trust).</div>
        </CardHeader>
        <CardContent className="p-4 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Group name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="pooled-name-input" /></div>
            <div><Label className="text-xs">Product code (optional)</Label><Input value={productCode} onChange={(e) => setProductCode(e.target.value.toUpperCase())} placeholder="BEO-CCB1-MSP" data-testid="pooled-product-input" /></div>
          </div>
          <div>
            <Label className="text-xs">Member customer names (one per line)</Label>
            <textarea className="w-full border border-slate-300 rounded-md p-2 font-mono text-sm min-h-[80px]" value={members} onChange={(e) => setMembers(e.target.value)} data-testid="pooled-members-input" />
          </div>
          <Button onClick={add} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="pooled-add-btn"><Plus className="h-4 w-4" /> Add group</Button>
        </CardContent>
      </Card>
      <Card className="border-slate-200"><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
            <tr><th className="px-3 py-2">Group</th><th className="px-3 py-2">Product</th><th className="px-3 py-2">Members</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((g) => (
              <tr key={g.id} className="border-b border-slate-100">
                <td className="px-3 py-2 font-medium">{g.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{g.product_code || <span className="text-slate-400 italic">any</span>}</td>
                <td className="px-3 py-2 text-xs">{(g.member_customer_names || []).join(", ") || <span className="text-slate-400 italic">none</span>}</td>
                <td className="px-3 py-2 text-right"><Button variant="outline" size="sm" className="text-red-600" onClick={() => del(g.id)} data-testid={`pooled-delete-${g.id}`}><Trash2 className="h-3 w-3" /></Button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="4" className="py-6 text-center text-sm text-slate-500">No pooled groups configured.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Product name aliases  (e.g. BEO-CCB1-MSP → Barracuda CCB)           */
/* ------------------------------------------------------------------ */
function ProductNamesTab() {
  const [items, setItems] = useState([]);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const r = await api.get("/barracuda/product-aliases");
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const c = (code || "").trim().toUpperCase();
    const n = (name || "").trim();
    if (!c || !n) return toast.error("Product code and friendly name are required");
    setBusy(true);
    try {
      await api.post("/barracuda/product-aliases", { product_code: c, friendly_name: n });
      toast.success(editingId ? "Updated" : "Saved");
      setCode(""); setName(""); setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const startEdit = (row) => { setEditingId(row.id); setCode(row.product_code); setName(row.friendly_name); };
  const cancelEdit = () => { setEditingId(null); setCode(""); setName(""); };
  const remove = async (row) => {
    if (!window.confirm(`Delete alias for ${row.product_code}?`)) return;
    await api.delete(`/barracuda/product-aliases/${row.id}`);
    toast.success("Deleted");
    await load();
  };

  return (
    <Card className="border-slate-200" data-testid="product-names-tab">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="text-base font-heading">Product name aliases</CardTitle>
        <div className="text-xs text-slate-500 mt-1">Rename cryptic Barracuda product codes (e.g. <span className="font-mono">BEO-CCB1-MSP</span>) with a friendly label that shows across the licence usage reports.</div>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr_auto_auto] gap-2 items-end">
          <div>
            <Label className="text-xs">Product code</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="BEO-CCB1-MSP" data-testid="product-alias-code" disabled={!!editingId} />
          </div>
          <div>
            <Label className="text-xs">Friendly name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Barracuda CCB" data-testid="product-alias-name" />
          </div>
          <Button onClick={save} disabled={busy} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="product-alias-save">
            <Save className="h-4 w-4" /> {editingId ? "Update" : "Add"}
          </Button>
          {editingId && (
            <Button variant="outline" onClick={cancelEdit} className="gap-2" data-testid="product-alias-cancel"><X className="h-4 w-4" /> Cancel</Button>
          )}
        </div>

        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Friendly name</th>
                <th className="px-3 py-2">Updated</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b border-slate-100" data-testid={`product-alias-row-${row.product_code}`}>
                  <td className="px-3 py-2 font-mono text-xs">{row.product_code}</td>
                  <td className="px-3 py-2 text-slate-800">{row.friendly_name}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtLocal(row.updated_at || row.created_at)}<div className="text-[10px] text-slate-400">by {row.updated_by || row.created_by || "—"}</div></td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(row)} data-testid={`product-alias-edit-${row.product_code}`}>Edit</Button>
                    <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => remove(row)} data-testid={`product-alias-delete-${row.product_code}`}>Delete</Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="4" className="py-8 text-center text-sm text-slate-500">No product aliases configured yet — add one above.</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}


/* ------------------------------------------------------------------ */
/* Mailbox                                                            */
/* ------------------------------------------------------------------ */
function MailboxTab() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => setCfg((await api.get("/barracuda/mailbox-config")).data);
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/barracuda/mailbox-config", cfg);
      toast.success("Mailbox settings saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const fetchNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/barracuda/fetch-now");
      if (r.data?.skipped) { toast.warning(`Skipped: ${r.data.reason}`); load(); return; }
      if (r.data?.graph_error) { toast.error(`Graph error: ${r.data.graph_error}`); load(); return; }
      const imp = (r.data.imported || []).length;
      const err = (r.data.errors || []);
      if (err.length) toast.error(`Checked ${r.data.checked_messages || 0} · imported ${imp} · errors: ${(err[0].error || "").slice(0,160)}`);
      else toast.success(`Checked ${r.data.checked_messages || 0} messages · imported ${imp}`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d ? JSON.stringify(d).slice(0,200) : `Fetch failed (${e?.response?.status || "network"})`));
    }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">Barracuda mailbox ingestion</CardTitle>
          <div className="text-xs text-slate-500 mt-1">Read-only mailbox scan via Microsoft Graph. Never marks messages read, moves or deletes anything — attachments are downloaded, hashed and only imported if they pass validation.</div>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Mailbox</Label><Input value={cfg.mailbox || ""} onChange={(e) => setCfg({ ...cfg, mailbox: e.target.value })} data-testid="mailbox-input" /></div>
            <div><Label className="text-xs">Poll interval (minutes)</Label><Input type="number" min="5" value={cfg.poll_minutes || 60} onChange={(e) => setCfg({ ...cfg, poll_minutes: parseInt(e.target.value || "60", 10) })} data-testid="mailbox-poll-input" /></div>
            <div><Label className="text-xs">Sender pattern (regex, optional)</Label><Input value={cfg.sender_pattern || ""} onChange={(e) => setCfg({ ...cfg, sender_pattern: e.target.value })} placeholder="barracuda\\.com$" data-testid="mailbox-sender-input" /></div>
            <div><Label className="text-xs">Subject pattern (regex, optional)</Label><Input value={cfg.subject_pattern || ""} onChange={(e) => setCfg({ ...cfg, subject_pattern: e.target.value })} placeholder="Licence Usage" data-testid="mailbox-subject-input" /></div>
            <div className="col-span-2"><Label className="text-xs">Filename pattern (regex)</Label><Input value={cfg.filename_pattern || ""} onChange={(e) => setCfg({ ...cfg, filename_pattern: e.target.value })} placeholder="^RAW.*\\.csv$" data-testid="mailbox-filename-input" /></div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <Checkbox checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: !!v })} data-testid="mailbox-enabled" />
            <span>Enable scheduled ingestion</span>
          </label>
          <div className="flex gap-2 pt-2">
            <Button onClick={save} disabled={busy} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="mailbox-save-btn"><Save className="h-4 w-4" /> Save</Button>
            <Button variant="outline" onClick={fetchNow} disabled={busy} className="gap-2" data-testid="mailbox-fetch-btn"><Mail className="h-4 w-4" /> Check for new reports now</Button>
          </div>
          {(cfg.last_fetch_at || cfg.last_fetch_message) && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs">
              <div className="font-mono text-slate-500 uppercase tracking-wider">Last fetch</div>
              <div className="mt-1"><span className="text-slate-600">{fmtLocal(cfg.last_fetch_at)}</span> · <span className="text-slate-700 font-mono">{cfg.last_fetch_message || ""}</span></div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
